"use client";

import { useState, useEffect, useCallback, useRef } from 'react';
import Pagination from '../components/Pagination';
import { APIURL, GetAllUser } from '../components/Api';
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  Search,
  RefreshCw,
  Users,
  Settings,
  Play,
  Pause,
  CheckCircle,
  XCircle,
  Database,
  Code,
  X,
  Check,
  AlertCircle,
  BarChart3,
  Zap,
  Clock,
  Target,
  Lock,
  Unlock,
  ArrowUpDown,
  ListRestart,
  Save
} from 'lucide-react';

const SearchableSelect = ({ options, value, onChange, placeholder, error, disabled, emptyText = "No results found" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = options.filter(opt =>
    opt.label.toLowerCase().includes(search.toLowerCase())
  );

  const selectedOption = options.find(opt => String(opt.value) === String(value));

  return (
    <div className="relative" ref={wrapperRef}>
      <div
        className={`w-full px-2 py-1.5 border rounded text-sm bg-white cursor-pointer flex justify-between items-center ${error ? 'border-red-500' : 'border-gray-300'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        onClick={() => {
          if (!disabled) {
            setIsOpen(!isOpen);
            if (!isOpen) setSearch('');
          }
        }}
      >
        <span className={selectedOption ? 'text-black truncate pr-2' : 'text-gray-400 truncate pr-2'}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <svg className="w-4 h-4 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
      </div>

      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-60 flex flex-col">
          <div className="p-2 border-b sticky top-0 bg-white">
            <input
              type="text"
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500 text-black"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
          </div>
          <div className="overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map(opt => (
                <div
                  key={opt.value}
                  className="px-3 py-2 text-sm hover:bg-blue-50 cursor-pointer text-black"
                  onClick={() => {
                    onChange(opt.value);
                    setIsOpen(false);
                    setSearch('');
                  }}
                >
                  {opt.label}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-sm text-gray-500 text-center">{emptyText}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default function MasterDataRulesPage() {
  const [rules, setRules] = useState([]);
  const [users, setUsers] = useState([]);
  const [masterData, setMasterData] = useState([]);
  const [targetAudienceOptions, setTargetAudienceOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  // Assignment dialog state
  const [assignMode, setAssignMode] = useState('global'); // 'global' | 'selective'
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [assignedBy, setAssignedBy] = useState('admin');
  const [assignReason, setAssignReason] = useState('');
  const [assigning, setAssigning] = useState(false);

  const [showPerformanceDialog, setShowPerformanceDialog] = useState(false);
  const [selectedPerformanceRule, setSelectedPerformanceRule] = useState(null);
  const [showBulkExecutionDialog, setShowBulkExecutionDialog] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState(null);
  const [showToggleStatusConfirmModal, setShowToggleStatusConfirmModal] = useState(false);
  const [pendingToggleRule, setPendingToggleRule] = useState(null);
  const [bulkExecutionLoading, setBulkExecutionLoading] = useState(false);
  const [showExecuteConfirmModal, setShowExecuteConfirmModal] = useState(false);
  const [ruleToExecute, setRuleToExecute] = useState(null);
  const [bulkExecutionResult, setBulkExecutionResult] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [refreshing, setRefreshing] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [errors, setErrors] = useState({});

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);

  // Auth header helper to avoid 401s due to wrong token type
  const getAuthHeaders = () => {
    const token = localStorage.getItem('accessToken');
    const tokenType = localStorage.getItem('tokenType') || 'Bearer';
    return {
      'Authorization': token ? `${tokenType} ${token}` : '',
      'Content-Type': 'application/json'
    };
  };

  // Form data
  const [formData, setFormData] = useState({
    masterDataId: '',
    ruleType: 'ASSET_ASSIGNMENT',
    ruleName: '',
    ruleDescription: '',
    targetAudience: ''
  });



  const validateForm = () => {
    const newErrors = {};
    if (!formData.masterDataId) newErrors.masterDataId = 'This field is required';
    if (!formData.ruleType) newErrors.ruleType = 'This field is required';
    if (!formData.ruleName?.trim()) newErrors.ruleName = 'This field is required';
    if (!formData.targetAudience) newErrors.targetAudience = 'This field is required';
    if (!formData.ruleDescription?.trim()) newErrors.ruleDescription = 'This field is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Bulk execution form data
  const [bulkExecutionForm, setBulkExecutionForm] = useState({
    ruleType: 'ASSET_ASSIGNMENT',
    targetAudience: 'NEW_USERS'
  });

  const ruleTypeOptions = [
    { label: 'Asset Assignment', value: 'ASSET_ASSIGNMENT' },
    // { label: 'Addon Recommendation', value: 'ADDON_RECOMMENDATION' },
    // { label: 'Content Filtering', value: 'CONTENT_FILTERING' },
    // { label: 'Personalization', value: 'PERSONALIZATION' }
  ];


  const normalizeTargetAudience = (value) => {
    if (!value) return '';
    // Exact match to existing values
    const values = new Set(targetAudienceOptions.map(o => o.value));
    if (values.has(value)) return value;
    // Try enum-style: upper, spaces/hyphens to underscores
    const enumStyle = String(value).trim().toUpperCase().replace(/[\s-]+/g, '_');
    if (values.has(enumStyle)) return enumStyle;
    // Try label match (case-insensitive)
    const byLabel = targetAudienceOptions.find(o => String(o.label).toLowerCase() === String(value).toLowerCase());
    if (byLabel) return byLabel.value;
    return '';
  };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  const fetchRules = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${APIURL}/api/v1/admin/master-data-rules`, {
        headers
      });

      if (response.ok) {
        const data = await response.json();
        setRules(data);
      } else {
        showToast('Failed to fetch master data rules', 'error');
      }
    } catch (error) {
      showToast('Error fetching master data rules', 'error');
    } finally {
      if (isRefresh) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  }, []);

  const handleRefresh = () => {
    fetchRules(true);
    fetchUsers();
    fetchMasterData();
  };

  useEffect(() => {
    fetchRules();
    fetchUsers();
    fetchMasterData();
    fetchTargetAudiences();
  }, [fetchRules]);

  const fetchTargetAudiences = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${APIURL}/api/v1/admin/master-data-rules/target-audiences`, {
        headers
      });

      if (response.ok) {
        const data = await response.json();
        // Map the API response to options with displayName as the label
        const options = data.map(item => ({
          label: item.displayName, // Use displayName as the label
          value: item.value // Use value as the value
        }));
        setTargetAudienceOptions(options);
      } else {
        console.error('Failed to fetch target audiences:', response.status);

      }
    } catch (error) {
      console.error('Error fetching target audiences:', error);

    }
  };

  const fetchUsers = async (isRefresh = false) => {
    try {
      const result = await GetAllUser();
      // API may return in different shapes: { data: { data: [...] } } or { data: [...] } or [...]
      const raw = result?.data?.data ?? result?.data ?? result ?? [];
      const list = Array.isArray(raw) ? raw : (raw?.content || []);

      setUsers(list.map(user => ({
        label: `${user.firstName ?? ''} ${user.lastName ?? ''} (${user.email ?? ''})`.trim(),
        value: user.userId ?? user.id
      })));

    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // Fetch users when the Assign dialog opens so the Selective tab has data immediately
  useEffect(() => {
    if (showAssignDialog) {
      fetchUsers();
    }
  }, [showAssignDialog]);

  // Also refresh when user switches to selective while dialog is open
  useEffect(() => {
    if (showAssignDialog && assignMode === 'selective') {
      fetchUsers();
    }
  }, [assignMode, showAssignDialog]);

  const fetchMasterData = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${APIURL}/api/v1/admin/master-data`, {
        headers
      });

      if (response.ok) {
        const data = await response.json();
        // Normalize response shapes and only include active master data items
        const raw = data?.data ?? data;
        const list = Array.isArray(raw) ? raw : (raw?.content || []);
        const activeItems = list.filter(item =>
          item?.isActive === true &&
          (item?.data_type || item?.dataType) !== 'NUTRITION_PLAN'
        );
        const formatDataType = (type) => {
          if (type === 'PDF_DOCUMENT') return 'Document';
          if (type === 'VIDEO_CONTENT') return 'Video Content';
          if (type === 'IMAGE_ASSET') return 'Image Asset';
          return type;
        };
        setMasterData(activeItems.map(item => ({
          label: `${item.name} (${formatDataType(item.data_type || item.dataType)})`,
          value: item.id
        })));
      }
    } catch (error) {
      console.error('Error fetching master data:', error);
    }
  };

  const handleCreate = async () => {

    if (!validateForm()) return;

    try {
      const headers = getAuthHeaders();
      const payload = {
        masterDataId: formData.masterDataId ? Number(formData.masterDataId) : undefined,
        ruleType: formData.ruleType,
        ruleName: formData.ruleName,
        ruleDescription: formData.ruleDescription || undefined,
        targetAudience: formData.targetAudience || null
      };
      const response = await fetch(`${APIURL}/api/v1/admin/master-data-rules`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        showToast('Master data rule created successfully');
        setShowCreateDialog(false);
        resetForm();
        fetchRules();
      } else {
        showToast('Failed to create master data rule', 'error');
      }
    } catch (error) {
      showToast('Error creating master data rule', 'error');
    }
  };

  const handleUpdate = async () => {
    if (!validateForm()) return;

    try {
      const headers = getAuthHeaders();
      // Backend UpdateRuleRequest only accepts: ruleType, ruleName, ruleDescription, targetAudience, isActive
      const payload = {
        ruleType: formData.ruleType || undefined,
        ruleName: formData.ruleName,
        ruleDescription: formData.ruleDescription || undefined,
        targetAudience: formData.targetAudience || null,
        isActive: typeof selectedRule?.isActive === 'boolean' ? selectedRule.isActive : undefined
      };
      const response = await fetch(`${APIURL}/api/v1/admin/master-data-rules/${selectedRule.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        showToast('Master data rule updated successfully');
        setShowEditDialog(false);
        resetForm();
        fetchRules();
      } else {
        showToast('Failed to update master data rule', 'error');
      }
    } catch (error) {
      showToast('Error updating master data rule', 'error');
    }
  };

  const handleDelete = async (id) => {
    setRuleToDelete(id);
    setShowDeleteConfirmModal(true);
  }

  const executeDelete = async (id) => {
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${APIURL}/api/v1/admin/master-data-rules/${id}`, {
        method: 'DELETE',
        headers
      });

      if (response.ok) {
        showToast('Master data rule deleted successfully');
        fetchRules();
      } else {
        showToast('Failed to delete master data rule', 'error');
      }
    } catch (error) {
      showToast('Error deleting master data rule', 'error');
    }
    setRuleToDelete(null);
  };

  const handleActivate = async (id) => {
    setPendingToggleRule({ id, action: 'activate' });
    setShowToggleStatusConfirmModal(true);
  };

  const handleDeactivate = async (id) => {
    setPendingToggleRule({ id, action: 'deactivate' });
    setShowToggleStatusConfirmModal(true);
  };

  const executeToggleStatus = async () => {
    if (!pendingToggleRule) return;

    const { id, action } = pendingToggleRule;
    const isActivate = action === 'activate';
    const url = `${APIURL}/api/v1/admin/master-data-rules/${id}/${isActivate ? 'activate' : 'deactivate'}`;

    try {
      const headers = getAuthHeaders();
      const response = await fetch(url, {
        method: 'PUT',
        headers
      });

      if (response.ok) {
        showToast(`Master data rule ${isActivate ? 'activated' : 'deactivated'} successfully`);
        fetchRules();
      } else {
        showToast(`Failed to ${isActivate ? 'activate' : 'deactivate'} master data rule`, 'error');
      }
    } catch (error) {
      showToast(`Error ${isActivate ? 'activating' : 'deactivating'} master data rule`, 'error');
    }
    setShowToggleStatusConfirmModal(false);
    setPendingToggleRule(null);
  };

  const handleExecuteForUser = async (userId) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${APIURL}/api/v1/admin/master-data-rules/execute/user/${userId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        let body = null;
        try { body = await response.json(); } catch (e) { /* ignore */ }

        const ok = body ? (
          Array.isArray(body) ? body.every(r => r.successfulRules > 0 || r.successRate > 0 || (r.ruleResults && r.ruleResults.some(rr => rr.success))) :
            (body.successfulRules ?? 0) > 0 ||
            body.success === true ||
            (body.ruleResults && body.ruleResults.some(r => r.success))
        ) : true;

        if (ok) {
          showToast('Rules executed for user successfully');
        } else {
          const errMsg = Array.isArray(body) ? 'Rules execution had some or all failures' : (body?.ruleResults?.find(r => !r.success)?.errorMessage || body?.message || 'Rules execution failed for user');
          showToast(errMsg, 'error');
        }
      } else {
        showToast('Failed to execute rules for user', 'error');
      }
    } catch (error) {
      showToast('Error executing rules for user', 'error');
    }
  };

  const handleExecuteForAllUsers = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${APIURL}/api/v1/admin/master-data-rules/execute/all-users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        let body = null;
        try { body = await response.json(); } catch (e) { /* ignore */ }

        const ok = body ? (
          Array.isArray(body) ? body.length > 0 : // If it's an array and we got it, it's usually a successful process of multiple users
            (body.successfulRules ?? 0) > 0 ||
            body.success === true ||
            (body.ruleResults && body.ruleResults.some(r => r.success))
        ) : true;

        if (ok) {
          showToast('All active rules are executed successfully');
        } else {
          const errMsg = body?.individualResults?.[0]?.ruleResults?.[0]?.errorMessage || body?.resultMessage || body?.message || 'Rules execution failed for all users';
          showToast(errMsg, 'error');
        }
      } else {
        showToast('Failed to execute rules for all users', 'error');
      }
    } catch (error) {
      showToast('Error executing rules for all users', 'error');
    }
  };

  // Assign selected rule to users (global or selective)
  const handleAssignRule = async () => {
    if (!selectedRule) {
      showToast('No rule selected', 'error');
      return;
    }

    setAssigning(true);

    try {
      const headers = getAuthHeaders();

      if (assignMode === "global") {
        // Use existing execute endpoint for all users
        const response = await fetch(`${APIURL}/api/v1/admin/master-data-rules/execute/all-users`, {
          method: 'POST',
          headers
        });

        if (response.ok) {
          // parse response body — backend may respond with result summary
          let body = null;
          try { body = await response.json(); } catch (e) { /* ignore */ }

          const ok = body ? (
            Array.isArray(body) || // Handle array response
            body.ruleResults?.some(r => r.ruleName === selectedRule.ruleName) ||
            (body.successfulRules ?? 0) > 0 ||
            body.success === true
          ) : response.ok;

          if (ok) {
            showToast('Rule assigned to all users successfully');
            setShowAssignDialog(false);
          } else {
            const msg = body?.individualResults?.[0]?.ruleResults?.[0]?.errorMessage || body?.resultMessage || body?.message || 'Failed to assign rule to all users';
            showToast(msg, 'error');
          }
        } else {
          showToast('Failed to assign rule to all users', 'error');
        }

      } else {
        // selective
        if (!selectedUserIds || selectedUserIds.length === 0) {
          showToast('Please select at least one user', 'error');
          setAssigning(false);
          return;
        }

        const results = await Promise.all(selectedUserIds.map(async (userId) => {
          try {
            const res = await fetch(`${APIURL}/api/v1/admin/master-data-rules/execute/user/${userId}`, {
              method: 'POST',
              headers,
              body: JSON.stringify({ ruleId: selectedRule.id, assignedBy, assignReason })
            });

            // consider HTTP OK but body indicating failure
            if (!res.ok) {
              return { userId, ok: false, message: `HTTP ${res.status}` };
            }

            let body = null;
            try { body = await res.json(); } catch (e) { /* ignore */ }

            const ok = body ? (
              body.ruleResults?.some(r => r.ruleName === selectedRule.ruleName)
            ) : res.ok;

            if (ok) {
              return { userId, ok: true, message: 'Assigned' };
            }

            // Extract useful error message if available
            const ruleResult = body?.ruleResults?.find(r => r.ruleName === selectedRule.ruleName);
            const msg = ruleResult?.message || body?.message || 'Rule not applied (may not match target audience)';
            return { userId, ok: false, message: msg };

          } catch (err) {
            return { userId, ok: false, message: err.message || 'Network error' };
          }
        }));

        const successCount = results.filter(r => r.ok).length;
        const failed = results.filter(r => !r.ok);
        const failedCount = failed.length;

        if (successCount > 0) showToast(`${successCount} users assigned successfully`);
        if (failedCount > 0) {
          // show aggregated error; include first failure message for clarity
          const firstMsg = failed[0]?.message || `${failedCount} assignments failed`;
          showToast(`${failedCount} assignments failed: ${firstMsg}`, 'error');
          console.warn('Assignment failures:', failed);
        }

        // close dialog if at least one success
        if (successCount > 0) {
          setShowAssignDialog(false);
          setSelectedUserIds([]);
        }
      }

      // refresh rule info
      fetchRules();
    } catch (error) {
      console.error('Error assigning rule:', error);
      showToast('Error assigning rule', 'error');
    } finally {
      setAssigning(false);
    }
  };

  const handleBulkExecute = async () => {
    setBulkExecutionLoading(true);
    setBulkExecutionResult(null);

    try {
      const headers = getAuthHeaders();
      const params = new URLSearchParams({
        ruleType: bulkExecutionForm.ruleType,
        targetAudience: bulkExecutionForm.targetAudience
      });

      const response = await fetch(`${APIURL}/api/v1/admin/master-data-rules/execute/bulk-enum?${params}`, {
        method: 'POST',
        headers
      });

      if (response.ok) {
        const result = await response.json();
        setBulkExecutionResult(result);
        showToast(`Bulk execution completed! ${result.totalUsersProcessed} users processed, ${result.totalAssignmentsCreated} assignments created`);
        fetchRules(); // Refresh rules to update statistics
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        showToast(`Failed to execute bulk rules: ${errorData.message || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      console.error('Error during bulk execution:', error);
      showToast('Error executing bulk rules', 'error');
    } finally {
      setBulkExecutionLoading(false);
    }
  };

  const openExecuteConfirm = (rule) => {
    setRuleToExecute(rule);
    setShowExecuteConfirmModal(true);
  };

  const handleExecuteRule = async () => {
    if (!ruleToExecute) return;
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${APIURL}/api/v1/admin/master-data-rules/${ruleToExecute.id}/execute`, {
        method: 'POST',
        headers
      });

      if (response.ok) {
        let body = null;
        try { body = await response.json(); } catch (e) { /* ignore */ }

        const ok = body ? (
          Array.isArray(body) ? body.length > 0 :
            body.successful === true || body.success === true || (body.successfulRules ?? 0) > 0 || (body.ruleResults && body.ruleResults.some(r => r.success))
        ) : true;

        if (ok) {
          const successMsg = body?.resultMessage || `Rule "${ruleToExecute.ruleName}" executed successfully`;
          showToast(successMsg);
          fetchRules();
        } else {
          const errMsg = body?.individualResults?.[0]?.ruleResults?.[0]?.errorMessage || body?.resultMessage || body?.message || 'Rule execution failed';
          showToast(errMsg, 'error');
        }
      } else {
        showToast('Failed to execute rule', 'error');
      }
    } catch (error) {
      showToast('Error executing rule', 'error');
    } finally {
      setShowExecuteConfirmModal(false);
      setRuleToExecute(null);
    }
  };

  const resetBulkExecutionForm = () => {
    setBulkExecutionForm({
      ruleType: 'ASSET_ASSIGNMENT',
      targetAudience: 'NEW_USERS'
    });
    setBulkExecutionResult(null);
  };

  const resetForm = () => {
    setFormData({
      masterDataId: '',
      ruleType: 'ASSET_ASSIGNMENT',
      ruleName: '',
      ruleDescription: '',
      targetAudience: ''
    });
    setErrors({});
  };

  const openEditDialog = (data) => {
    setSelectedRule(data);
    setFormData({
      masterDataId: data.masterDataId || '',
      ruleType: data.ruleType || 'ASSET_ASSIGNMENT',
      ruleName: data.ruleName || '',
      ruleDescription: data.ruleDescription || '',

      targetAudience: normalizeTargetAudience(data.targetAudience)
    });
    setErrors({});
    setShowEditDialog(true);
  };

  const openViewDialog = (data) => {
    setSelectedRule(data);
    setShowViewDialog(true);
  };

  const openAssignDialog = (data) => {
    setSelectedRule(data);
    // reset assignment dialog state to sensible defaults
    setAssignMode('global');
    setSelectedUserIds([]);
    setAssignedBy('admin');
    setAssignReason('');
    setAssigning(false);
    setShowAssignDialog(true);
  }

  const openPerformanceDialog = (data) => {
    setSelectedPerformanceRule(data);
    setShowPerformanceDialog(true);
  };

  const filteredData = (() => {
    let filtered = rules.filter(item => {
      const matchesSearch = item.ruleName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.ruleDescription?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.ruleType?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = !filterStatus ||
        (filterStatus === 'active' && item.isActive) ||
        (filterStatus === 'inactive' && !item.isActive);

      return matchesSearch && matchesStatus;
    });

    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue, bValue;

        if (sortConfig.key === 'ruleName') {
          aValue = (a.ruleName || '').toLowerCase();
          bValue = (b.ruleName || '').toLowerCase();
        } else if (sortConfig.key === 'ruleType') {
          aValue = (a.ruleType || '').toLowerCase();
          bValue = (b.ruleType || '').toLowerCase();
        } else if (sortConfig.key === 'masterDataName') {
          aValue = (a.masterDataName || '').toLowerCase();
          bValue = (b.masterDataName || '').toLowerCase();
        } else if (sortConfig.key === 'targetAudience') {
          aValue = (a.targetAudience || '').toLowerCase();
          bValue = (b.targetAudience || '').toLowerCase();
        } else if (sortConfig.key === 'status') {
          aValue = a.isActive ? 'active' : 'inactive';
          bValue = b.isActive ? 'active' : 'inactive';
        } else {
          aValue = a[sortConfig.key];
          bValue = b[sortConfig.key];
        }

        if (aValue == null) aValue = '';
        if (bValue == null) bValue = '';

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  })();

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get sort indicator
  const getSortIndicator = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'asc' ? '↑' : '↓';
    }
    return '';
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentRules = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus]);

  return (
    <div className="p-4">
      {/* Toast Notification */}
      {toast.show && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'
          }`}>
          <div className="flex items-center gap-2">
            {toast.type === 'error' ? <AlertCircle className="w-5 h-5" /> : <Check className="w-5 h-5" />}
            {toast.message}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex flex-col lg:flex-row justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-start gap-4">
                <div className="bg-indigo-100 p-3 rounded-lg">
                  <Code className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <p className="text-gray-600 text-sm leading-relaxed max-w-3xl">
                    It is a rule engine having functionality for both manual and automatic assigning of pdfs/videos based on the user's plan, also analyze the performance of rules.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg transition-all duration-200 disabled:cursor-not-allowed text-sm font-medium shadow-sm hover:shadow focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                aria-label={refreshing ? "Refreshing data" : "Refresh rules"}
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
              </button>
              <button
                onClick={() => {
                  setShowCreateDialog(true);
                  setErrors({});
                  resetForm();
                  setFormData(prev => ({ ...prev, masterDataId: '', ruleType: 'ASSET_ASSIGNMENT', ruleName: '', ruleDescription: '', targetAudience: '' }));
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg flex items-center gap-2 transition-colors text-sm font-medium shadow-sm hover:shadow focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
              >
                <Plus className="w-4 h-4" />
                New Rule
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-4">
        <div className="flex justify-between items-center">
          <div className="flex gap-2">
            <button
              onClick={handleExecuteForAllUsers}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
            >
              <Play className="w-4 h-4" />
              Execute All Rules
            </button>
            <button
              onClick={() => setShowBulkExecutionDialog(true)}
              className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center gap-2"
            >
              <Zap className="w-4 h-4" />
              Smart Bulk Execute
            </button>
          </div>

          <div className="flex gap-2">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sl. No.</th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('ruleName')}
                  >
                    <div className="flex items-center">
                      Rule Name
                      <ArrowUpDown className="ml-1 w-4 h-4" />
                      <span className="ml-1">{getSortIndicator('ruleName')}</span>
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('ruleType')}
                  >
                    <div className="flex items-center">
                      Type
                      <ArrowUpDown className="ml-1 w-4 h-4" />
                      <span className="ml-1">{getSortIndicator('ruleType')}</span>
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('masterDataName')}
                  >
                    <div className="flex items-center">
                      Master Data Name
                      <ArrowUpDown className="ml-1 w-4 h-4" />
                      <span className="ml-1">{getSortIndicator('masterDataName')}</span>
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('targetAudience')}
                  >
                    <div className="flex items-center">
                      Target Audience
                      <ArrowUpDown className="ml-1 w-4 h-4" />
                      <span className="ml-1">{getSortIndicator('targetAudience')}</span>
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center">
                      Status
                      <ArrowUpDown className="ml-1 w-4 h-4" />
                      <span className="ml-1">{getSortIndicator('status')}</span>
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  <th></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentRules.map((item, index) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{indexOfFirstItem + index + 1}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.ruleName}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center gap-2">
                        <Code className="w-4 h-4" />
                        <span>{(item.ruleType || '').replace(/_/g, " ") || '-'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900 max-w-xs truncate">{item.masterDataName || '-'}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{item.targetAudience || '-'}</td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${item.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                        {item.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex gap-2">
                        <button
                          onClick={() => openViewDialog(item)}
                          className="text-blue-600 hover:text-blue-900"
                          title="View"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openEditDialog(item)}
                          className="text-yellow-600 hover:text-yellow-900"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>

                        {/* <button
                            onClick={() => openAssignDialog(item)}
                            className="text-purple-600 hover:text-purple-900"
                            title="Assign to Users"
                          >
                            <Users className="w-4 h-4" />
                          </button> */}

                        {item.isActive ? (
                          <button
                            onClick={() => handleDeactivate(item.id)}
                            className="text-yellow-600 hover:text-yellow-900"
                            title="Deactivate"
                          >
                            <Lock className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleActivate(item.id)}
                            className="text-green-600 hover:text-green-900"
                            title="Activate"
                          >
                            <Unlock className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openPerformanceDialog(item)}
                          className="text-purple-600 hover:text-purple-900"
                          title="View Performance"
                        >
                          <BarChart3 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                    <td>
                      <button
                        onClick={() => openExecuteConfirm(item)}
                        className='bg-pink-600 text-white px-3 py-1.5 rounded-lg hover:bg-pink-600 text-sm font-medium'
                      >
                        Execute
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {filteredData.length === 0 && !loading && (
          <div className="p-8 text-center text-gray-500">
            <Code className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>No master data rules found</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalPages={totalPages}
        totalElements={filteredData.length}
        pageSize={itemsPerPage}
        setPageSize={setItemsPerPage}
        itemName="Rules"
      />

      {/* Create Dialog */}
      {showCreateDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl text-black font-bold">Create Master Data Rule</h2>
              <button
                onClick={() => {
                  setShowCreateDialog(false);
                  setErrors({});
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Compact row with 3 fields */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-black mb-1">Master Data *</label>
                  <SearchableSelect
                    options={masterData}
                    value={formData.masterDataId}
                    onChange={(val) => {
                      setFormData({ ...formData, masterDataId: val });
                      if (errors.masterDataId) setErrors({ ...errors, masterDataId: null });
                    }}
                    placeholder="Select Data"
                    error={errors.masterDataId}
                    emptyText={masterData.length < 1 ? "No active Master Data Found" : "No results found"}
                  />
                  {errors.masterDataId && <p className="text-red-500 text-xs mt-1">{errors.masterDataId}</p>}
                </div>

                <div>
                  <label className="block text-xs font-medium text-black mb-1">Rule Type *</label>
                  <select
                    value={formData.ruleType}
                    onChange={(e) => {
                      setFormData({ ...formData, ruleType: e.target.value });
                      if (errors.ruleType) setErrors({ ...errors, ruleType: null });
                    }}
                    className={`w-full px-2 py-1.5 border rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black ${errors.ruleType ? 'border-red-500' : 'border-gray-300'}`}
                  >
                    <option value="" disabled hidden>Select Rule Type</option>
                    {ruleTypeOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  {errors.ruleType && <p className="text-red-500 text-xs mt-1">{errors.ruleType}</p>}
                </div>

                <div>
                  <label className="block text-xs font-medium text-black mb-1">Target Audience *</label>
                  <select
                    value={formData.targetAudience}
                    onChange={(e) => {
                      setFormData({ ...formData, targetAudience: e.target.value });
                      if (errors.targetAudience) setErrors({ ...errors, targetAudience: null });
                    }}
                    className={`w-full px-2 py-1.5 border rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black ${errors.targetAudience ? 'border-red-500' : 'border-gray-300'}`}
                  >
                    <option value="" disabled hidden>Select audience</option>
                    {targetAudienceOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  {errors.targetAudience && <p className="text-red-500 text-xs mt-1">{errors.targetAudience}</p>}
                </div>
              </div>

              <div>
                <label className="block text-black text-sm font-medium mb-2">Rule Name *</label>
                <input
                  type="text"
                  value={formData.ruleName}
                  onChange={(e) => {
                    setFormData({ ...formData, ruleName: e.target.value });
                    if (errors.ruleName) setErrors({ ...errors, ruleName: null });
                  }}
                  placeholder="Enter rule name"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black ${errors.ruleName ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.ruleName && <p className="text-red-500 text-xs mt-1">{errors.ruleName}</p>}
              </div>

              <div>
                <label className="block text-black text-sm font-medium mb-2">Rule Description *</label>
                <input
                  type="text"
                  value={formData.ruleDescription}
                  onChange={(e) => {
                    setFormData({ ...formData, ruleDescription: e.target.value });
                    if (errors.ruleDescription) setErrors({ ...errors, ruleDescription: null });
                  }}
                  placeholder="Enter rule description"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black ${errors.ruleDescription ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.ruleDescription && <p className="text-red-500 text-xs mt-1">{errors.ruleDescription}</p>}
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setShowCreateDialog(false);
                  setErrors({});
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Dialog */}
      {showEditDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-black">Edit Master Data Rule</h2>
              <button
                onClick={() => {
                  setShowEditDialog(false);
                  setErrors({});
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Compact row with 3 fields */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-black mb-1">Master Data *</label>
                  <SearchableSelect
                    options={masterData}
                    value={formData.masterDataId}
                    onChange={(val) => {
                      setFormData({ ...formData, masterDataId: val });
                      if (errors.masterDataId) setErrors({ ...errors, masterDataId: null });
                    }}
                    placeholder="Select Data"
                    error={errors.masterDataId}
                  />
                  {errors.masterDataId && <p className="text-red-500 text-xs mt-1">{errors.masterDataId}</p>}
                </div>

                <div>
                  <label className="block text-xs font-medium text-black mb-1">Rule Type *</label>
                  <select
                    value={formData.ruleType}
                    onChange={(e) => {
                      setFormData({ ...formData, ruleType: e.target.value });
                      if (errors.ruleType) setErrors({ ...errors, ruleType: null });
                    }}
                    className={`w-full px-2 py-1.5 border rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black ${errors.ruleType ? 'border-red-500' : 'border-gray-300'}`}
                  >

                    {ruleTypeOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  {errors.ruleType && <p className="text-red-500 text-xs mt-1">{errors.ruleType}</p>}
                </div>

                <div>
                  <label className="block text-xs font-medium text-black mb-1">Target Audience *</label>
                  <select
                    value={formData.targetAudience}
                    onChange={(e) => {
                      setFormData({ ...formData, targetAudience: e.target.value });
                      if (errors.targetAudience) setErrors({ ...errors, targetAudience: null });
                    }}
                    className={`w-full px-2 py-1.5 border rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black ${errors.targetAudience ? 'border-red-500' : 'border-gray-300'}`}
                  >
                    <option value="" disabled hidden>Select audience</option>
                    {targetAudienceOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  {errors.targetAudience && <p className="text-red-500 text-xs mt-1">{errors.targetAudience}</p>}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-black mb-2">Rule Name *</label>
                <input
                  type="text"
                  value={formData.ruleName}
                  onChange={(e) => {
                    setFormData({ ...formData, ruleName: e.target.value });
                    if (errors.ruleName) setErrors({ ...errors, ruleName: null });
                  }}
                  placeholder="Enter rule name"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black ${errors.ruleName ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.ruleName && <p className="text-red-500 text-xs mt-1">{errors.ruleName}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-black mb-2">Rule Description *</label>
                <input
                  type="text"
                  value={formData.ruleDescription}
                  onChange={(e) => {
                    setFormData({ ...formData, ruleDescription: e.target.value });
                    if (errors.ruleDescription) setErrors({ ...errors, ruleDescription: null });
                  }}
                  placeholder="Enter rule description"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black ${errors.ruleDescription ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.ruleDescription && <p className="text-red-500 text-xs mt-1">{errors.ruleDescription}</p>}
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setShowEditDialog(false);
                  setErrors({});
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Dialog */}
      {showViewDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-800">View Master Data Rule</h2>
              <button onClick={() => setShowViewDialog(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            {selectedRule && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600">ID</label>
                    <p className="text-sm text-gray-600">{selectedRule.id}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Master Data Name</label>
                    <p className="text-sm text-gray-600">{selectedRule.masterDataName || '-'}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Rule Name</label>
                    <p className="text-sm font-semibold text-gray-600">{selectedRule.ruleName}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Rule Type</label>
                    <p className="text-sm text-gray-600">{(selectedRule.ruleType || '').replace(/_/g, " ") || '-'}</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600">Rule Description</label>
                  <p className="text-sm text-gray-600">{selectedRule.ruleDescription ? selectedRule.ruleDescription : '-'}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Target Audience</label>
                    <p className="text-sm text-gray-600">{selectedRule.targetAudience || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Create At</label>
                    <p className="text-sm text-gray-600">{new Date(selectedRule.createdAt).toLocaleDateString()}</p>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                    Performance Metrics
                  </h3>

                  <div className="grid grid-cols-3 gap-6">
                    <div className="text-center">
                      <div className="bg-white rounded-lg p-4 shadow-sm border border-blue-100">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
                          <span className="text-blue-600 font-bold text-sm">E</span>
                        </div>
                        <p className="text-xs text-gray-600 mb-1">Total Executions</p>
                        <p className="text-2xl font-bold text-blue-600">{selectedRule.executionCount || 0}</p>
                      </div>
                    </div>

                    <div className="text-center">
                      <div className="bg-white rounded-lg p-4 shadow-sm border border-green-100">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                          <span className="text-green-600 font-bold text-sm">S</span>
                        </div>
                        <p className="text-xs text-gray-600 mb-1">Successful</p>
                        <p className="text-2xl font-bold text-green-600">{selectedRule.successCount || 0}</p>
                      </div>
                    </div>

                    <div className="text-center">
                      <div className={`bg-white rounded-lg p-4 shadow-sm border ${(selectedRule.successRate || 0) >= 0.8 ? 'border-green-100' :
                        (selectedRule.successRate || 0) >= 0.6 ? 'border-yellow-100' :
                          'border-red-100'
                        }`}>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-2 ${(selectedRule.successRate || 0) >= 0.8 ? 'bg-green-100' :
                          (selectedRule.successRate || 0) >= 0.6 ? 'bg-yellow-100' :
                            'bg-red-100'
                          }`}>
                          <span className={`font-bold text-sm ${(selectedRule.successRate || 0) >= 0.8 ? 'text-green-600' :
                            (selectedRule.successRate || 0) >= 0.6 ? 'text-yellow-600' :
                              'text-red-600'
                            }`}>%</span>
                        </div>
                        <p className="text-xs text-gray-600 mb-1">Success Rate</p>
                        <p className={`text-2xl font-bold ${(selectedRule.successRate || 0) >= 0.8 ? 'text-green-600' :
                          (selectedRule.successRate || 0) >= 0.6 ? 'text-yellow-600' :
                            'text-red-600'
                          }`}>
                          {((selectedRule.successRate || 0)).toFixed(1)}%
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            )}
          </div>
        </div>
      )}

      {/* Assign Dialog */}
      {showAssignDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">

            {/* Header */}
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                Assign Rule to Users
              </h2>
              <button
                onClick={() => {
                  setShowAssignDialog(false);
                  setAssignMode('global');
                  setSelectedUserIds([]);
                  setAssignedBy('admin');
                  setAssignReason('');
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Body */}
            <div className="space-y-4">
              {/* Assignment Mode */}
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-800">
                  Assignment Mode
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setAssignMode('global')}
                    className={`px-3 py-2 rounded border ${assignMode === 'global'
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-800 border-gray-300'
                      }`}
                  >
                    Global
                  </button>

                  <button
                    onClick={() => setAssignMode('selective')}
                    className={`px-3 py-2 rounded border ${assignMode === 'selective'
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-800 border-gray-300'
                      }`}
                  >
                    Selective
                  </button>
                </div>
              </div>

              {/* Global Info */}
              {assignMode === 'global' && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    This rule will be applied to <strong>all users</strong>.
                  </p>
                </div>
              )}

              {/* Selective Users */}
              {assignMode === 'selective' && (
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-800">
                    Select Users
                  </label>

                  <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-lg p-2">
                    {users.map((user) => (
                      <label
                        key={user.value}
                        className="flex items-center space-x-2 p-1 hover:bg-gray-50"
                      >
                        <input
                          type="checkbox"
                          checked={selectedUserIds.includes(user.value)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedUserIds(prev => [...prev, user.value]);
                            } else {
                              setSelectedUserIds(prev =>
                                prev.filter(id => id !== user.value)
                              );
                            }
                          }}
                          className="rounded"
                        />
                        <span className="text-sm text-gray-800">
                          {user.label}
                        </span>
                      </label>
                    ))}
                  </div>

                  <p className="text-xs text-gray-500 mt-1">
                    Selected: {selectedUserIds.length} users
                  </p>
                </div>
              )}

              {/* Metadata */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-800">
                    Assigned By
                  </label>
                  <input
                    type="text"
                    value={assignedBy}
                    onChange={(e) => setAssignedBy(e.target.value)}
                    placeholder="admin"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-black"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-800">
                    Reason
                  </label>
                  <input
                    type="text"
                    value={assignReason}
                    onChange={(e) => setAssignReason(e.target.value)}
                    placeholder="Reason for assignment"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-black"
                  />
                </div>
              </div>
            </div>

            {/* Footer ✅ now inside modal */}
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setShowAssignDialog(false);
                  setAssignMode('global');
                  setSelectedUserIds([]);
                  setAssignedBy('admin');
                  setAssignReason('');
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>

              <button
                onClick={handleAssignRule}
                disabled={assigning}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {assigning ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Assigning...</span>
                  </>
                ) : (
                  <span>Assign</span>
                )}
              </button>
            </div>

          </div>
        </div>
      )}



      {/* Performance Dialog */}
      {showPerformanceDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl text-black font-bold flex items-center gap-2">
                <BarChart3 className="w-6 h-6 text-purple-600" />
                Performance Analytics
              </h2>
              <button onClick={() => setShowPerformanceDialog(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            {selectedPerformanceRule && (
              <div className="space-y-6">
                {/* Rule Info */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Rule Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-600">Rule Name</label>
                      <p className="text-sm font-semibold text-gray-800">{selectedPerformanceRule.ruleName}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600">Rule Type</label>
                      <p className="text-sm font-semibold text-gray-800">{(selectedPerformanceRule.ruleType || '').replace(/_/g, " ") || '-'}</p>
                    </div>
                  </div>
                </div>

                {/* Performance Metrics */}
                <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-100">
                  <h3 className="text-lg font-semibold text-gray-800 mb-6 flex items-center gap-2">
                    <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                    Performance Metrics
                  </h3>

                  <div className="grid grid-cols-3 gap-6">
                    <div className="text-center">
                      <div className="bg-white rounded-xl p-6 shadow-sm border border-blue-100 hover:shadow-md transition-shadow">
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                          <BarChart3 className="w-6 h-6 text-blue-600" />
                        </div>
                        <p className="text-sm text-gray-600 mb-2">Total Executions</p>
                        <p className="text-3xl font-bold text-blue-600">{selectedPerformanceRule.executionCount || 0}</p>
                        <p className="text-xs text-gray-500 mt-1">Times executed</p>
                      </div>
                    </div>

                    <div className="text-center">
                      <div className="bg-white rounded-xl p-6 shadow-sm border border-green-100 hover:shadow-md transition-shadow">
                        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                          <CheckCircle className="w-6 h-6 text-green-600" />
                        </div>
                        <p className="text-sm text-gray-600 mb-2">Successful</p>
                        <p className="text-3xl font-bold text-green-600">{selectedPerformanceRule.successCount || 0}</p>
                        <p className="text-xs text-gray-500 mt-1">Completed successfully</p>
                      </div>
                    </div>

                    <div className="text-center">
                      <div className={`bg-white rounded-xl p-6 shadow-sm border hover:shadow-md transition-shadow ${(selectedPerformanceRule.successRate || 0) >= 0.8 ? 'border-green-100' :
                        (selectedPerformanceRule.successRate || 0) >= 0.6 ? 'border-yellow-100' :
                          'border-red-100'
                        }`}>
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 ${(selectedPerformanceRule.successRate || 0) >= 0.8 ? 'bg-green-100' :
                          (selectedPerformanceRule.successRate || 0) >= 0.6 ? 'bg-yellow-100' :
                            'bg-red-100'
                          }`}>
                          <span className={`text-lg font-bold ${(selectedPerformanceRule.successRate || 0) >= 0.8 ? 'text-green-600' :
                            (selectedPerformanceRule.successRate || 0) >= 0.6 ? 'text-yellow-600' :
                              'text-red-600'
                            }`}>%</span>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">Success Rate</p>
                        <p className={`text-3xl font-bold ${(selectedPerformanceRule.successRate || 0) >= 80 ? 'text-green-600' :
                          (selectedPerformanceRule.successRate || 0) >= 60 ? 'text-yellow-600' :
                            'text-red-600'
                          }`}>
                          {((selectedPerformanceRule.successRate || 0)).toFixed(1)}%
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {((selectedPerformanceRule.successRate || 0)) >= 80 ? 'Excellent' :
                            ((selectedPerformanceRule.successRate || 0)) >= 60 ? 'Good' :
                              'Needs Improvement'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Additional Info */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">Additional Information</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <label className="block text-gray-600">Target Audience</label>
                      <p className="font-medium text-gray-800">{selectedPerformanceRule.targetAudience || '-'}</p>
                    </div>
                    <div>
                      <label className="block text-gray-600">Status</label>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${selectedPerformanceRule.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                        {selectedPerformanceRule.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bulk Execution Dialog */}
      {showBulkExecutionDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                <Zap className="w-6 h-6 text-purple-600" />
                Smart Bulk Rule Execution
              </h2>
              <button
                onClick={() => { setShowBulkExecutionDialog(false); resetBulkExecutionForm(); }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Information Card */}
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-4 border border-purple-100">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                    <Target className="w-4 h-4 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">Smart Targeting</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Execute rules for users matching specific criteria (e.g New Users within last 3 days).
                      This prevents outdated assignments to long-time users.
                    </p>
                  </div>
                </div>
              </div>

              {/* Form */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rule Type
                  </label>
                  <select
                    value={bulkExecutionForm.ruleType}
                    onChange={(e) => setBulkExecutionForm({ ...bulkExecutionForm, ruleType: e.target.value })}
                    className="w-full px-3 py-2 border text-black border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >

                    {ruleTypeOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Target Audience
                  </label>
                  <select
                    value={bulkExecutionForm.targetAudience}
                    onChange={(e) => setBulkExecutionForm({ ...bulkExecutionForm, targetAudience: e.target.value })}
                    className="w-full px-3 py-2 border text-black border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    {targetAudienceOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {bulkExecutionForm.targetAudience === 'NEW_USERS' && (
                    <p className="text-xs text-blue-600 mt-1">
                      ⚡ Only targets users created within the last 3 days
                    </p>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handleBulkExecute}
                  disabled={bulkExecutionLoading}
                  className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {bulkExecutionLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Executing...
                    </>
                  ) : (
                    <>
                      <Zap className="w-4 h-4" />
                      Execute Rules
                    </>
                  )}
                </button>

                <button
                  onClick={() => { setShowBulkExecutionDialog(false); resetBulkExecutionForm(); }}
                  className="bg-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>

              {/* Results */}
              {bulkExecutionResult && (
                <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-4 border border-green-200">
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2 mb-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    Execution Results
                  </h3>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="bg-white rounded-lg p-3 border border-green-100">
                      <div className="flex items-center gap-2 text-gray-600 mb-1">
                        <Users className="w-4 h-4" />
                        Users Processed
                      </div>
                      <div className="text-2xl font-bold text-green-600">
                        {bulkExecutionResult.totalUsersProcessed}
                      </div>
                    </div>

                    <div className="bg-white rounded-lg p-3 border border-blue-100">
                      <div className="flex items-center gap-2 text-gray-600 mb-1">
                        <Database className="w-4 h-4" />
                        Assignments Created
                      </div>
                      <div className="text-2xl font-bold text-blue-600">
                        {bulkExecutionResult.totalAssignmentsCreated}
                      </div>
                    </div>



                    <div className="bg-white rounded-lg p-3 border border-purple-100">
                      <div className="flex items-center gap-2 text-gray-600 mb-1">
                        <Clock className="w-4 h-4" />
                        Duration
                      </div>
                      <div className="text-sm font-medium text-purple-600">
                        {(bulkExecutionResult.executionDurationMs / 1000).toFixed(2)}s
                      </div>
                    </div>
                  </div>

                  {bulkExecutionResult.resultMessage && (
                    <div className="bg-white rounded-lg p-3 mt-3 border border-gray-200">
                      <p className="text-sm text-gray-700">{bulkExecutionResult.resultMessage}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-red-600">Confirm Deletion</h2>
              <button
                onClick={() => {
                  setShowDeleteConfirmModal(false);
                  // setPendingAction(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete this master data? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirmModal(false);
                  setRuleToDelete(null);
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  executeDelete(ruleToDelete);
                  setShowDeleteConfirmModal(false);
                  setRuleToDelete(null);
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {showToggleStatusConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-orange-600">Confirm Status Change</h2>
              <button
                onClick={() => {
                  setShowToggleStatusConfirmModal(false);
                  setPendingToggleRule(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <p className="text-gray-700 mb-6">
              {pendingToggleRule && pendingToggleRule.action === 'deactivate'
                ? 'Are you sure you want to deactivate this master data rule?'
                : 'Are you sure you want to activate this master data rule?'}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowToggleStatusConfirmModal(false);
                  setPendingToggleRule(null);
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={executeToggleStatus}
                className={`px-4 py-2 text-white rounded-lg ${pendingToggleRule && pendingToggleRule.action === 'deactivate'
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-green-600 hover:bg-green-700'
                  }`}
              >
                {pendingToggleRule && pendingToggleRule.action === 'deactivate' ? 'Deactivate' : 'Activate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showExecuteConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-blue-600">Confirm Execute</h2>
              <button
                onClick={() => {
                  setShowExecuteConfirmModal(false);
                  setRuleToExecute(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <p className="text-gray-700 mb-6">
              Are you sure you want to execute the rule <strong>{ruleToExecute?.ruleName}</strong>. This rule will apply to {ruleToExecute?.targetAudience || 'target audience'} immediately.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowExecuteConfirmModal(false);
                  setRuleToExecute(null);
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleExecuteRule}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Execute
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
